import React, { useState } from 'react';
import type { Plant } from '../../../types/plant';
import { Heart, Sun, Droplets, Users, Clock, Plus, Check, Loader2, Sprout } from 'lucide-react';
import PlantZoneCompatibility from './PlantZoneCompatibility';
import { useGarden } from '../../../hooks/useGarden';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import GardenSuccessModal from '../../modals/GardenSuccessModal';
import GardenLoginModal from '../../modals/GardenLoginModal';
import { isFrostSensitive, isColdHardy } from '../../../utils/dateUtils';

interface PlantHeroProps {
  plant: Plant;
  isInGarden?: boolean;
}

export default function PlantHero({ plant, isInGarden = false }: PlantHeroProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToGarden, loading } = useGarden();
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAddedToGarden, setIsAddedToGarden] = useState(isInGarden);

  const handleAddToGarden = async () => {
    setError(null);
    
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      await addToGarden(plant.id);
      setIsAddedToGarden(true);
      setShowSuccessModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to garden');
    }
  };

  // Format maturity time range
  const getMaturityRange = () => {
    if (plant.time_seed_to_maturity_min && plant.time_seed_to_maturity_max) {
      if (plant.time_seed_to_maturity_min === plant.time_seed_to_maturity_max) {
        return `${plant.time_seed_to_maturity_min} days`;
      }
      return `${plant.time_seed_to_maturity_min}-${plant.time_seed_to_maturity_max} days`;
    }
    return 'Not specified';
  };

  // Get direct sow description based on frost tolerance
  const getDirectSowDescription = () => {
    if (isFrostSensitive(plant.frost_tolerance)) {
      return "Direct sow on last frost date";
    } else if (isColdHardy(plant.frost_tolerance)) {
      const days = plant.direct_sow_cold_hardy_days_before_frost_max || 14;
      return `Can direct sow ${days} days before last frost`;
    } else {
      return "Direct sow after last frost";
    }
  };

  return (
    <div className="relative h-[50vh] min-h-[400px] max-h-[500px] bg-gray-900">
      {/* Hero Image with Gradient Overlay */}
      <div className="absolute inset-0">
        <img 
          src={plant.image_url} 
          alt={plant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
      </div>

      {/* Content */}
      <div className="absolute inset-0 flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {plant.name}
            </h1>
            <p className="text-xl text-gray-200 italic mb-6">
              {plant.scientific_name}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Sun className="h-5 w-5 text-white" />
                <div>
                  <p className="text-gray-300 text-xs">Sunlight</p>
                  <p className="text-white text-sm">{plant.sunlight_requirements}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Droplets className="h-5 w-5 text-white" />
                <div>
                  <p className="text-gray-300 text-xs">Water</p>
                  <p className="text-white text-sm">{plant.water_requirements}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-white" />
                <div>
                  <p className="text-gray-300 text-xs">Time to Maturity</p>
                  <p className="text-white text-sm">{getMaturityRange()}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-white" />
                <div>
                  <p className="text-gray-300 text-xs">Difficulty</p>
                  <p className="text-white text-sm capitalize">{plant.difficulty_level}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex-1">
                <PlantZoneCompatibility plant={plant} light />
              </div>

              {/* Direct Sow Information */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex-1">
                <div className="flex items-center gap-2">
                  <Sprout className="h-5 w-5 text-white" />
                  <div>
                    <p className="text-gray-300 text-xs">Direct Sowing</p>
                    <p className="text-white text-sm">{getDirectSowDescription()}</p>
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}

              {!isAddedToGarden ? (
                <button
                  onClick={handleAddToGarden}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-5 w-5" />
                      <span>Add to My Garden</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-lg min-w-[180px]">
                  <Check className="h-5 w-5" />
                  <span>In Your Garden</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GardenSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        plantName={plant.name}
        plantId={plant.id}
      />

      <GardenLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        plantName={plant.name}
      />
    </div>
  );
}